import { useEffect, useState } from "react";
import { Label } from "@mui/icons-material";
import { Box, FormControl, Grid2, MenuItem, Select, Stack, Typography } from "@mui/material";
import Checkbox, { checkboxClasses } from "@mui/material/Checkbox";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import formatTriggerName from "../../../helpers/formatTriggerNamesWithWhitespaces";
import useAuthHeader from "../../../hooks/useAuthHeader";
import { useRuleWizardContext } from "../../../providers/RuleWizardProvider";
import {
	RuleFormulaSummary, RuleFormulaSummaryItem, RuleSummary,
	WizardHeaderTag,
	WizardStepContainer
} from "../styled/rule-wizard.styled";

/**
 * Step 5 in Rule Creation Wizard
 * User confirms Rule prior to persistence
*/
export default function WizardStep5() {
	const authHeader = useAuthHeader();

	const {
		editedId, name, description, 
		groups, tags, ruleLocation, 
		itemsInFormula, setGroups
	} = useRuleWizardContext();

	const [groupOptions, setGroupOptions] = useState([]);
	const [ruleGroups, setRuleGroups] = useState([]);

	const handleGroupsChange = (event) => {
		setRuleGroups(event.target.value);
		setGroups(event.target.value);
	};

	const initGroups = () => {
		axios.get(apiRoutes.getGroups, {
			headers: authHeader,
			params: { name: "" }
		}).then(res => {
			setGroupOptions(res.data);
		}).catch(err => {
			console.log(err);
		});
	};

	useEffect(() => {
		initGroups();
		if (editedId) {
			setRuleGroups(groups);
		}

	}, []);

	const renderFormulaItem = (item) => {
		return (
			<RuleFormulaSummaryItem key={item.index}>
				{item.triggerType === "backend" ? item.friendlyName : formatTriggerName(item.triggerName)}
			</RuleFormulaSummaryItem>
		);
	};

	return (
		<WizardStepContainer>
			<Stack spacing={2}>
				<Typography variant="h6">Review the rule</Typography>
				<Grid2 item xs={12}>
					<RuleSummary>
						<Stack direction="row" spacing={2}>
							<Typography sx={{ fontWeight: 600 }}>Rule name: </Typography>
							<Typography>{name}</Typography>
						</Stack>
						<Stack direction="row" spacing={2}>
							<Typography sx={{ fontWeight: 600 }}>Rule description: </Typography>
							<Typography>{description}</Typography>
						</Stack>
						<Stack direction="row" spacing={2}>
							{
								!ruleLocation || !ruleLocation.name || ruleLocation.name === "" ?
									<>
										<Typography sx={{ fontWeight: 600 }}>Rule reaction location: </Typography>
										<Typography>No location selected</Typography>
									</>
									:
									<>
										<Typography sx={{ fontWeight: 600 }}>Rule reaction location: </Typography>
										<Typography>{ruleLocation.name}</Typography>
									</>
							}
						</Stack>

						<Typography sx={{ fontWeight: 600 }}>Formula: </Typography>
						<RuleFormulaSummary>
							{
								itemsInFormula.map(renderFormulaItem)
							}
						</RuleFormulaSummary>
					</RuleSummary>

					<RuleSummary>
						<Typography>What groups do you want to assign the rule to?</Typography>
						<FormControl fullWidth>
							<Select id="user-select-group" value={ruleGroups} label="Groups"
								onChange={handleGroupsChange} multiple renderValue={(selected) => selected.join(", ")}>
								{
									groupOptions.map(group =>
										<MenuItem key={group.id} value={group.name}>
											<Checkbox checked={ruleGroups.indexOf(group.name) > -1} sx={{
												[`&.${checkboxClasses.checked}`]: {
													color: "#A9A9A9",
												}
											}} />
											{group.name}
										</MenuItem>
									)
								}
							</Select>
						</FormControl>
					</RuleSummary>

					<RuleSummary>
						<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }}>
							<Typography sx={{ fontWeight: 600, marginRight: "10px" }}>Tags: </Typography>
							{
								tags.map((tag, index) =>
									<WizardHeaderTag key={index} sx={{ marginRight: 3 }}>
										<Label fontSize="small" sx={{ paddingRight: 1 }} />
										{tag.toUpperCase()}
									</WizardHeaderTag>
								)
							}
						</Box>
					</RuleSummary>
				</Grid2>
			</Stack>
		</WizardStepContainer>
	);
}